'use client';
import PaginatedGrid, { GridType } from '@components/PaginatedGrid';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { useState, memo, useRef, useEffect } from 'react';
import GridItem from '@components/StoriesGridItem';
import useUpdateEffect from '@hooks/useUpdateEffect';
import Section from '@repo/ui/src/components/Section';
import { fetchStoriesClient, parseSortOption } from './fetchCategoryDataClient';
import { classNames } from '@repo/ui/src/utils/classNames';

import type { Data } from '@typings/productGrid';

interface Category {
  name: string;
  value: string;
}

interface SortOption {
  value: string;
  label: string;
}

interface StoriesGridProps {
  title?: string;
  filters?: {
    categories?: Array<Category>;
  };
  sortOptions?: Array<SortOption>;
  _data?: Data;
}

const StoriesGrid = ({ title, filters, sortOptions, _data }: StoriesGridProps) => {
  const { locale } = useCurrentUrl();
  const [data, setData] = useState(_data);
  const [currentTab, setCurrentTab] = useState(0);
  const [currentCategory, setCurrentCategory] = useState(
    filters?.categories?.length ? filters?.categories[0].value : null,
  );
  const [loading, setLoading] = useState(false);
  const tabsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const indicatorRef = useRef<HTMLDivElement>(null);

  const sort = parseSortOption(sortOptions?.[0]?.value);

  const loadData = async ({ page }: { page: number }): Promise<any> => {
    return await fetchStoriesClient({
      locale,
      page,
      sort,
      category: currentCategory,
    });
  };

  const selectTab = (categoryValue: string, index: number): void => {
    setCurrentTab(index);
    setCurrentCategory(categoryValue);
  };

  useEffect(() => {
    if (indicatorRef.current && tabsRef.current[currentTab]) {
      const activeTab = tabsRef.current[currentTab];
      if (activeTab) {
        const { offsetLeft, offsetWidth } = activeTab;
        indicatorRef.current.style.left = `${offsetLeft}px`;
        indicatorRef.current.style.width = `${offsetWidth}px`;
      }
    }
  }, [currentTab]);

  useUpdateEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const res = await loadData({ page: 0 });
      setData(res);
      setLoading(false);
    };
    fetchData();
  }, [currentCategory]);

  return (
    <Section gutterX basePadding white>
      {title && <h2 className="text-center tra-text-web-heading-2">{title}</h2>}
      {filters?.categories && (
        <div className="flex justify-center py-2 md:py-4">
          <div className="overflow-x-auto flex py-5 px-3 scrollbar-hide relative">
            {filters?.categories.map((category, i) => (
              <button
                key={`${category.value}_${i}`}
                className={classNames(
                  'mx-3 tra-text-web-heading-5',
                  currentTab === i ? 'text-primary-600' : 'text-surface-450',
                )}
                onClick={() => selectTab(category.value, i)}
                ref={(el) => {
                  tabsRef.current[i] = el;
                }}
              >
                {category.name}
              </button>
            ))}
            <div
              ref={indicatorRef}
              className="absolute bottom-3 h-1 bg-action-300 transition-all duration-300 ease-in-out"
              aria-hidden="true"
            />
          </div>
        </div>
      )}
      {loading && (
        <div className="flex justify-center relative">
          <div className="absolute top-[50px] z-10 h-[100px] w-[100px] border-8 border-action-400 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      <PaginatedGrid
        _data={data}
        loadData={loadData}
        GridItemComponent={GridItem}
        type={GridType.Story}
        darkBackground
        disableBorder
        content={{}}
        hideAddToCartButton={false}
      />
    </Section>
  );
};

export default memo(StoriesGrid);
